import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SG_GET_CAMPAIGNS, UPDATE_CAMPAIGN_PAGE_NUMBER } from 'constants/actions';
import { Link } from 'react-router-dom';
import PageIntro from '../components/PageIntro';

export default function Index() {
  const dispatch = useDispatch();
  const campaigns = useSelector((s) => s.action_campaign?.campaigns || []);
  const pageNumber = useSelector((s) => s.action_campaign?.pageNumber || 1);

  const [page, setPage] = useState(pageNumber);

  useEffect(() => {
    const query = `page=${page}`;
    dispatch({ type: SG_GET_CAMPAIGNS, payload: query });
    dispatch({ type: UPDATE_CAMPAIGN_PAGE_NUMBER, payload: page });
  }, [dispatch, page]);

  // support both paginated ({ results, count, next }) and plain array responses
  const list = Array.isArray(campaigns) ? campaigns : campaigns?.results || [];
  const total = Array.isArray(campaigns) ? list.length : campaigns?.count || list.length;
  const hasNext = !Array.isArray(campaigns) ? Boolean(campaigns?.next) : false;

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  return (
    <div>
      <PageIntro
        title="Campaigns"
        description="Campaigns are the starting point for Action Planning: they group reports, events and related actions for an initiative."
        bullets={[
          'Where data comes from: created by admins (optionally from debrief reports).',
          'What to expect: manager reports, scheduled events, and derived actions.',
          'Next steps: start report generation, schedule planning events, or add managers.'
        ]}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.length ? (
          list.map((c) => {
            const bg = c.status === 'draft' ? '#f8f9fa' : (c.status === 'report_generation' ? '#eef7ff' : '#ffffff');
            const border = c.status === 'draft' ? '#e9ecef' : (c.status === 'report_generation' ? '#dfefff' : '#eee');

            return (
              <div key={c.id} style={{ padding: 12, border: `1px solid ${border}`, borderRadius: 8, background: bg, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      <div style={{ color: '#666', fontSize: 12, whiteSpace: 'nowrap' }}>{c.organization?.name ? `• ${c.organization.name}` : ''}</div>
                      <div style={{ color: '#999', fontSize: 11, marginTop: 4 }}>Campaign id: {c.id} • Org id: {c.organization?.id ?? '-'}</div>
                    </div>
                    <div style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 999, background: c.status === 'draft' ? '#fff' : '#eaf5ff', border: '1px solid #e0eaf7', color: c.status === 'draft' ? '#666' : '#0b5fff', fontWeight: 700, fontSize: 12 }}>{c.status}</div>
                    {c.id && c.id !== 'undefined' ? (
                      <Link to={`${c.id}`} style={{ textDecoration: 'none', color: '#0b5fff', fontWeight: 700 }}>View</Link>
                    ) : (
                      <div style={{ color: '#9ca3af' }}>No id</div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ color: '#666', fontSize: 13 }}>{c.created_by_name || c.created_by || '-'}</div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ padding: '6px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 6, fontSize: 12 }}>Managers: <strong style={{ marginLeft: 6 }}>{c.managers_count ?? (c.campaign_stats?.total_managers ?? '-')}</strong></div>
                    <div style={{ padding: '6px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 6, fontSize: 12 }}>Reports: <strong style={{ marginLeft: 6 }}>{c.reports_count ?? (c.campaign_stats?.reports_generated ?? '-')}</strong></div>
                    <div style={{ padding: '6px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 6, fontSize: 12 }}>Events: <strong style={{ marginLeft: 6 }}>{c.events_count ?? (c.campaign_stats?.events_active ?? '-')}</strong></div>
                    <div style={{ padding: '6px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 6, fontSize: 12 }}>Actions: <strong style={{ marginLeft: 6 }}>{c.actions_count ?? (c.campaign_stats?.actions_total ?? '-')}</strong></div>
                    <div style={{ padding: '6px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 6, fontSize: 12 }}>Completion: <strong style={{ marginLeft: 6 }}>{typeof c.completion_rate !== 'undefined' ? `${c.completion_rate}%` : (c.campaign_stats?.avg_progress ? `${c.campaign_stats.avg_progress}%` : '-')}</strong></div>
                    <div style={{ padding: '6px 8px', background: '#fff', border: '1px solid #eee', borderRadius: 6, fontSize: 12 }}>Avg progress: <strong style={{ marginLeft: 6 }}>{typeof c.avg_action_progress !== 'undefined' ? `${c.avg_action_progress}%` : (c.campaign_stats?.avg_progress ? `${c.campaign_stats.avg_progress}%` : '-')}</strong></div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ padding: 18, border: '1px dashed #eee', borderRadius: 8, color: '#666' }}>No campaigns</div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>Previous</button>
        <span style={{ margin: '0 12px' }}>Page {page} — {total} items</span>
        <button onClick={() => setPage(page + 1)} disabled={!hasNext}>Next</button>
      </div>
    </div>
  );
}
